import { useContext } from 'react'
import {
    Box,
    Typography,
    Paper,
    Autocomplete,
    TextField,
    IconButton,
    Button,
    Tooltip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material'
import {
    HighlightOff as HighlightOffIcon,
    ExpandMore as ExpandMoreIcon,
    WarningAmberRounded,
} from '@mui/icons-material'
import { Grid } from '.'
import { osmosisGoogleMapAddressApi } from '../../config/osmosisConfig'

import GooglePlacesAutocomplete, {
    geocodeByAddress,
    getLatLng,
} from 'react-google-places-autocomplete'
import { OSMO, RADIUS, SHADOW_SOFT } from '../../config/brandTokens'

import { GoogleMapsWrapper } from './hooks/GoogleMapsApiWrapper'
import { Authentication } from '../../context/AuthenticationContext'

interface UniversalFormSectionTypes {
    title?: string
    icon?: any
    size?: number
    save?: any
    reset?: any
    titleBottom?: any
    titleAlignment?: 'left' | 'center' | 'right'
    titleBottomAlignment?: 'left' | 'center' | 'right'
    spacing?: number
    sx?: {}
    children?: React.ReactNode
}

export const UniversalFormSection = ({
    title,
    size = 12,
    titleAlignment = 'left',
    spacing = 2,
    sx = { padding: 3 },
    children,
    icon,
    save,
    reset,
    titleBottom,
    titleBottomAlignment = 'center',
}: UniversalFormSectionTypes) => {
    return (
        <Grid item xs={size} sx={{ marginBottom: 2 }}>
            <Paper sx={sx} elevation={4}>
                <Grid container spacing={spacing}>
                    {(save || reset) && (
                        <Grid item xs={12} md={12}>
                            <Grid
                                container
                                sx={{
                                    marginBottom: -2,
                                    justifyContent: 'flex-end',
                                }}
                            >
                                <Grid item xs={1}>{save}</Grid>
                                {reset}
                            </Grid>
                        </Grid>
                    )}

                    {title && (
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.25,
                                    mb: 1.5,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 4,
                                        height: 18,
                                        borderRadius: 1,
                                        flexShrink: 0,
                                        background:
                                            'linear-gradient(180deg, #8BC441 0%, #5C8529 100%)',
                                        boxShadow: '0 0 8px rgba(139,196,65,0.45)',
                                    }}
                                />
                                <Typography
                                    align={titleAlignment}
                                    variant="subtitle2"
                                    sx={{
                                        fontWeight: 700,
                                        letterSpacing: '0.08em',
                                        textTransform: 'uppercase',
                                        color: 'text.primary',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                    }}
                                >
                                    {icon}
                                    {title}
                                </Typography>
                            </Box>
                        </Grid>
                    )}

                    {children}

                    {titleBottom && (
                        <Grid item xs={12} sx={{ marginTop: 1 }}>
                            <Typography
                                align={titleBottomAlignment}
                                variant="subtitle2"
                                color={'text.secondary'}
                            >
                                {titleBottom}
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </Paper>
        </Grid>
    )
}

interface UniversalAutoCompleteProps {
    options: string[]
    value: any
    handleChange: any
    label: string
    name: string
    error?: boolean
    helperText?: string
    required?: boolean
    id?: string
    fullWidth?: boolean
    sx?: any
    onBlur?: any
    InputProps?: any
    getOptionLabel?: any
    disabled?: boolean
    clearFunction?: any
    additionalFunction?: any
    disableClearable?: boolean
    customIcon?: any
    multiple?: boolean
    isOptionEqualToValue?: any
    renderTags?: any
    renderOption?: any
    onTextFieldChange?: any
    onInputChange?: (e: any, newValue: any) => void
    placeholder?: string
    noOptionsText?: string
    filterOptions?: any
    size?: 'small' | 'medium'
}

export const UniversalAutoComplete = ({
    options,
    value,
    handleChange,
    onBlur,
    label,
    name,
    error,
    required,
    helperText,
    id,
    sx,
    InputProps,
    fullWidth = true,
    getOptionLabel,
    disabled = false,
    clearFunction,
    disableClearable,
    customIcon,
    multiple,
    isOptionEqualToValue,
    renderTags,
    renderOption,
    onTextFieldChange,
    additionalFunction,
    onInputChange,
    placeholder,
    noOptionsText,
    filterOptions,
    size = 'medium',
}: UniversalAutoCompleteProps) => {
    return (
        <Autocomplete
            multiple={multiple}
            disablePortal
            size={size}
            id={id}
            options={options}
            value={value}
            onBlur={onBlur}
            disabled={disabled}
            renderTags={renderTags}
            renderOption={renderOption}
            filterSelectedOptions
            filterOptions={filterOptions}
            noOptionsText={noOptionsText}
            isOptionEqualToValue={isOptionEqualToValue}
            disableClearable={disableClearable || disabled || !!clearFunction}
            getOptionLabel={getOptionLabel}
            onChange={(event, newValue) => {
                const data = {
                    target: {
                        name,
                        value: newValue,
                    },
                }
                handleChange(data)
            }}
            onInputChange={onInputChange}
            fullWidth={fullWidth}
            sx={sx}
            renderInput={(params) => (
                <TextField
                    {...params}
                    name={name}
                    label={label}
                    helperText={helperText}
                    error={error}
                    onChange={onTextFieldChange}
                    required={required}
                    variant="outlined"
                    value={value}
                    placeholder={placeholder}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment:
                            clearFunction &&
                            params.inputProps.value &&
                            !disabled ? (
                                <>
                                    {params.InputProps.endAdornment}
                                    <IconButton onClick={clearFunction}>
                                        <HighlightOffIcon />
                                    </IconButton>
                                    {additionalFunction && (
                                        <IconButton
                                            onClick={additionalFunction}
                                        >
                                            {customIcon}
                                        </IconButton>
                                    )}
                                </>
                            ) : (
                                params.InputProps.endAdornment
                            ),
                    }}
                />
            )}
        />
    )
}

interface UniversalTitle {
    title: string
    variant?: any
    color?: 'primary' | 'secondary'
    sx?: {}
    align?: 'center' | 'left' | 'right' | 'inherit' | 'justify'
}

export const UniversalTitle = ({
    title,
    variant = 'h1',
    color = 'primary',
    sx = { margin: 2 },
    align = 'center',
}: UniversalTitle) => {
    return (
        <Typography variant={variant} color={color} align={align} sx={sx}>
            {title}
        </Typography>
    )
}

// Universal Google Maps Autocomplete

interface UniversalGoogleMapsProps {
    value: any
    onBlur?: any
    label: string
    name?: string
    id?: string
    fullWidth?: boolean
    sx?: any
    onChange?: (location: any) => void
    onClick?: any
    clearFunction?: any
    disabled?: any
}

export const UniversalGoogleMapsAc = ({
    value,
    onChange,
    label,
    clearFunction,
    disabled,
    onBlur,
}: UniversalGoogleMapsProps) => {
    const handleSelect = async (address: any) => {
        try {
            const results = await geocodeByAddress(address.label)
            const latLng = await getLatLng(results[0])

            const location = {
                address: address.label,
                lat: latLng.lat,
                lng: latLng.lng,
            }

            onChange?.(location)
        } catch (error) {
            console.error('Error fetching location:', error)
        }
    }

    return (
        <Grid item xs={12} md={12}>
            <GoogleMapsWrapper>
                <Grid container spacing={2}>
                    <Grid item xs={10} md={10}>
                        <GooglePlacesAutocomplete
                            apiKey={osmosisGoogleMapAddressApi}
                            selectProps={{
                                value: value,
                                onChange: handleSelect,
                                isClearable: clearFunction,
                                placeholder: value ? value : label,
                                isDisabled: disabled,
                                onBlur: onBlur,

                                // Brand-aligned dark styling (OSMO surface +
                                // hairline border + green focus) to match the
                                // app's themed MUI inputs.
                                styles: {
                                    container: (provided) => ({
                                        ...provided,
                                        backgroundColor: 'transparent',
                                        borderRadius: RADIUS,
                                        width: '100%',
                                    }),
                                    control: (provided, { isFocused }) => ({
                                        ...provided,
                                        backgroundColor:
                                            'rgba(255,255,255,0.045)',
                                        border: `1px solid ${
                                            isFocused ? OSMO.green : OSMO.border
                                        }`,
                                        borderRadius: RADIUS,
                                        padding: '3px',
                                        minHeight: 48,
                                        boxShadow: isFocused
                                            ? `0 0 0 3px ${OSMO.greenSoft}`
                                            : 'none',
                                        transition:
                                            'border-color 0.15s ease, box-shadow 0.15s ease',
                                        '&:hover': {
                                            borderColor: 'rgba(255,255,255,0.28)',
                                        },
                                    }),
                                    option: (
                                        provided,
                                        { isFocused, isSelected }
                                    ) => ({
                                        ...provided,
                                        color: isSelected
                                            ? OSMO.green
                                            : 'rgba(255,255,255,0.85)',
                                        backgroundColor: isFocused
                                            ? 'rgba(255,255,255,0.06)'
                                            : isSelected
                                            ? OSMO.greenSoft
                                            : 'transparent',
                                        padding: '10px 15px',
                                        transition: 'background-color 0.15s ease',
                                        cursor: 'pointer',
                                    }),
                                    menu: (provided) => ({
                                        ...provided,
                                        backgroundColor: OSMO.surface,
                                        border: `1px solid ${OSMO.border}`,
                                        borderRadius: RADIUS,
                                        overflow: 'hidden',
                                        boxShadow: SHADOW_SOFT,
                                        zIndex: 1000,
                                    }),
                                    input: (provided) => ({
                                        ...provided,
                                        color: OSMO.white,
                                        padding: '8px 12px',
                                        outline: 'none',
                                        boxShadow: 'none',
                                    }),
                                    singleValue: (provided) => ({
                                        ...provided,
                                        color: OSMO.white,
                                        padding: '0 12px',
                                    }),
                                    placeholder: (provided) => ({
                                        ...provided,
                                        color: 'rgba(255,255,255,0.5)',
                                        padding: '0 12px',
                                    }),
                                },
                            }}
                        />
                    </Grid>
                    <Grid item xs={1} md={1}>
                        {value && (
                            <Tooltip title="Reset Address">
                                <IconButton
                                    onClick={clearFunction}
                                    disabled={disabled}
                                    sx={{
                                        height: 48,
                                        width: 48,
                                        color: 'text.secondary',
                                        '&:hover': { color: 'error.main' },
                                    }}
                                >
                                    <HighlightOffIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Grid>
                </Grid>
            </GoogleMapsWrapper>
        </Grid>
    )
}

interface UniversalSaveButtonProps {
    handleSubmit: (e: any) => void
    handleReset: () => void
    disabledSave: boolean
    disabledReset: boolean
    saveTitle?: string
    resetTitle?: string
    title?: string
    titleAlignment?: 'center' | 'left' | 'right'
    saveStyle?: {}
    resetStyle?: {}
    saveVariant?: 'contained' | 'text' | 'outlined'
    resetVariant?: 'contained' | 'text' | 'outlined'
    containerStyle?: {}
    saveColor?: 'primary' | 'secondary' | 'error'
    resetColor?: 'primary' | 'secondary' | 'error'
    saveSize?: number
    resetSize?: number
    quickStart?: boolean
    manufacturer?: any
    /** Hide the "Required fields are marked with *" helper (shown by default). */
    hideRequiredHint?: boolean
}
export const UniversalSaveButton = ({
    handleSubmit,
    handleReset,
    disabledSave,
    disabledReset,
    saveTitle,
    resetTitle,
    title,
    titleAlignment,
    saveStyle,
    resetStyle,
    saveVariant,
    resetVariant,
    containerStyle,
    saveColor,
    resetColor,
    saveSize,
    resetSize,
    quickStart,
    manufacturer,
    hideRequiredHint,
}: UniversalSaveButtonProps) => {
    const currentUser = useContext(Authentication)
    const { activeManufacturer } = currentUser.auth
    const manufacturerCheck = activeManufacturer === manufacturer
    const mismatch = manufacturer && !manufacturerCheck

    return (
        <Grid item xs={12} sx={{ marginBottom: 2 }}>
            <Paper
                elevation={0}
                sx={{
                    mt: 2,
                    px: { xs: 2, md: 3 },
                    py: 2,
                    borderRadius: `${RADIUS}px`,
                    backgroundColor: 'rgba(255,255,255,0.02)',
                    ...containerStyle,
                }}
            >
                {mismatch ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.75,
                            mb: 1.5,
                            color: 'error.main',
                            fontWeight: 600,
                            fontSize: 13,
                        }}
                    >
                        <WarningAmberRounded sx={{ fontSize: 18 }} />
                        Your account&apos;s active Manufacturer does not match
                        this form
                    </Box>
                ) : !hideRequiredHint ? (
                    <Typography
                        align={titleAlignment || 'center'}
                        sx={{
                            mb: 1.5,
                            fontSize: 12,
                            color: 'text.secondary',
                            '& b': { color: 'primary.main', fontWeight: 700 },
                        }}
                    >
                        Required fields are marked with <b>*</b>
                    </Typography>
                ) : null}

                <Grid container spacing={1.5}>
                    <Grid item xs={12} md={saveSize || 8}>
                        <Button
                            variant={saveVariant || 'contained'}
                            color={saveColor || 'primary'}
                            onClick={handleSubmit}
                            disabled={mismatch ? true : disabledSave}
                            fullWidth
                            sx={{
                                height: 46,
                                fontWeight: 700,
                                letterSpacing: '0.02em',
                                ...saveStyle,
                            }}
                        >
                            {(quickStart && 'Save & Go To Next Step') ||
                                saveTitle ||
                                'Save & Close'}
                        </Button>
                    </Grid>
                    <Grid item xs={12} md={resetSize || 4}>
                        <Button
                            variant={resetVariant || 'outlined'}
                            color={resetColor || 'inherit'}
                            onClick={handleReset}
                            disabled={mismatch ? true : disabledReset}
                            fullWidth
                            sx={{
                                height: 46,
                                fontWeight: 600,
                                color: resetColor ? undefined : 'text.secondary',
                                borderColor: resetColor
                                    ? undefined
                                    : 'divider',
                                ...resetStyle,
                            }}
                        >
                            {resetTitle || 'Reset'}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>
        </Grid>
    )
}

interface UniversalAccordionProps {
    title?: string
    expandMoreTitleDescription?: string
    accordionTitleDescription?: string
    accordionTitle?: any
    expanded?: boolean
    defaultExpanded?: boolean
    onClick?: () => void
    children: any
    titleAlignment?: any
    disabled?: any
    sx?: any
    summaryStyle?: any
    accordionStyle?: any
    fullSizeButton?: any
}

export const UniversalAccordion = ({
    accordionTitle,
    expandMoreTitleDescription,
    accordionTitleDescription,
    titleAlignment,
    disabled,
    expanded,
    defaultExpanded,
    onClick,
    sx,
    summaryStyle,
    accordionStyle,
    fullSizeButton,
    children,
}: UniversalAccordionProps) => {
    return (
        <Grid item xs={12} sx={sx}>
            <Accordion
                sx={accordionStyle}
                expanded={expanded}
                defaultExpanded={defaultExpanded}
            >
                <AccordionSummary
                    disabled={disabled}
                    sx={summaryStyle}
                    onClick={onClick}
                    expandIcon={
                        <Tooltip title={expandMoreTitleDescription}>
                            <ExpandMoreIcon />
                        </Tooltip>
                    }
                >
                    <Typography
                        variant="subtitle1"
                        title={accordionTitleDescription}
                    >
                        {accordionTitle}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>{children}</AccordionDetails>
            </Accordion>
        </Grid>
    )
}
